import Box from '@material-ui/core/Box';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import NyCheckboxInput from 'nyse-web-tools-common/lib/react/form/NyCheckboxInput/NyCheckboxInput';
import NyForm from 'nyse-web-tools-common/lib/react/form/NyForm/NyForm';
import NyResetBtn from 'nyse-web-tools-common/lib/react/form/NyResetBtn/NyResetBtn';
import NySubmitAlert from 'nyse-web-tools-common/lib/react/form/NySubmitAlert/NySubmitAlert';
import NySubmitBtn from 'nyse-web-tools-common/lib/react/form/NySubmitBtn/NySubmitBtn';
import React, {useCallback, useMemo} from 'react';

import { capitalizeRole } from '../../../../configs/entitlementRoleMap';
import useResource from '../../../../hooks/useResource.hook';
import {
    addRiskEntityFloorBrokerResource,
    deleteRiskEntityResource, RiskSettingsData
} from '../../../../resources/riskSettings.resource';
import {FloorBrokerFirmSelect} from '../../../selects/floorBrokerFirm.select';

type ConsentFormProps = {
    classes: {
        alert: string;
        controls: string;
        form: string;
        formBody: string;
    };
    className: string;
    data: {
        crd: number;
        displayRiskEntity: string;
        RiskSettingsData: RiskSettingsData;
        limitCrdRole: string;
        limitCrd: number;
        entityType: string;
        entityId?: number;
    };
    onSuccess: () => void;
};

export const FloorBrokerRiskEntityForm: React.FC<ConsentFormProps> = React.memo((props) => {

    const addRiskEntityResource = useResource(addRiskEntityFloorBrokerResource);
    const delRiskEntityResource = useResource(deleteRiskEntityResource);
    const [selectedFloorBrokerCrd, setSelectedFloorBrokerCrd] = React.useState<string>('');

    const consentValues = useMemo(()=> ({
        limitCrd: props.data.limitCrd,
        limitCrdRole: props.data.limitCrdRole,
        floorBrokerCrd: undefined
    }),[props.data.limitCrd, props.data.limitCrdRole]);

    const onConsentSubmit = useCallback(
        (values) => (props.data.entityType !== 'BROKER_MPID_SUBID')
            ? addRiskEntityResource.post(
                {
                    entityId: props.data.entityId,
                    limitCrd: props.data.limitCrd,
                    limitCrdRole: props.data.limitCrdRole,
                    floorBrokerCrd: values.floorBrokerCrd
                }
            )
            : delRiskEntityResource.delete(
                {
                    entityId: props.data.entityId,
                    limitCrd: props.data.limitCrd,
                    limitCrdRole: props.data.limitCrdRole
                }
            ),
        [
            addRiskEntityResource.post,
            delRiskEntityResource.delete,
            props.data.entityType,
            props.data.entityId,
            props.data.limitCrd,
            props.data.limitCrdRole
        ]
    );

    return (
        <NyForm
            className={`${props.classes.form} ${props.className}`}
            initialValues={consentValues}
            enableReinitialize
            onSuccess={props.onSuccess}
            onSubmit={onConsentSubmit}>
            <Grid className={props.classes.formBody} container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Box display='inline' fontWeight='fontWeightMedium'>
                        {capitalizeRole(props.data.limitCrdRole)}
                        {props.data.limitCrdRole === 'floor_broker'
                            ? ' (Entering Firm)'
                            : ' Firm'}
                        :
                    </Box>
                    &nbsp;{props.data.limitCrd}
                </Grid>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                {(props.data.entityType !== 'BROKER_MPID_SUBID') && (
                    <>
                        <Grid item xs={4} className='form-group'>
                            <label>Floor Broker: {selectedFloorBrokerCrd}</label>
                            <FloorBrokerFirmSelect
                                name='floorBrokerCrd'
                                required
                                onChange={(optionOrValue) => {
                                    const nextValue =
                                       typeof optionOrValue === 'string'
                                           ? optionOrValue
                                           : optionOrValue?.value ?? '';
                                    setSelectedFloorBrokerCrd(nextValue);
                                }}
                            />
                        </Grid>
                        <Grid item xs={8}/>
                    </>)}
                <Grid item xs={12}>
                    <Box fontWeight='fontWeightMedium'>
                        Entering Firm Consents To NYSE Floor Broker Firm Having The Following Entitlements
                    </Box>
                </Grid>
                <Grid item xs={12} className='form-group'>
                    <NyCheckboxInput name='consentOption' required>
                        {(props.data.entityType !== 'BROKER_MPID_SUBID')
                            ? 'Set Pre-Trade Risk Controls* for Entering Firm'
                            : 'None (cancels all entitlements for the NYSE Floor Broker Firm from the Entering Firm)'
                        }
                    </NyCheckboxInput>
                </Grid>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                <Grid item xs={12}>
                    <Box ml={1} mr={1}>
                        Entering Firm has indicated that NYSE Floor Broker Firm
                        will be entitled to the above-selected Pre-Trade Risk
                        Controls* on the Entering Firm’s trading activity on the
                        Exchange. If Entering Firm selects (a) from the
                        Entitlement List, Entering Firm: (i) expressly consents
                        to let the Exchange take direction from the NYSE Floor
                        Broker Firm about whether and to what extent to block and
                        cancel orders and suspend or terminate Entering Firm’s
                        access if Pre-Trade Risk Controls* set by the NYSE Floor
                        Broker Firm are breached; and (ii) agrees to hold the
                        Exchange harmless from direction taken from the NYSE Floor
                        Broker Firm in accordance with (i). This Entitlement
                        Consent remains in effect until canceled or modified by
                        Entering Firm by submitting a new Entitlement Consent or
                        by the Exchange.
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                <Grid item xs={12}>
                    <Box className='disclaimer' ml={1} mr={1} fontSize={10}>
                        *&nbsp;Note: For more information on Pre-Trade Risk
                        Controls, please see:&nbsp;
                        <a href='https://top.nyse.com' target='_blank' rel='noopener noreferrer'>
                            https://top.nyse.com
                        </a>
                        , which may be updated from time to time. The Exchange
                        will take commercially reasonable efforts to provide
                        notice of any changes to the available Pre-Trade Risk
                        Controls via trader notice.
                    </Box>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Divider/>
                </Grid>
                <Grid
                    item
                    xs={12}
                    component={NySubmitAlert}
                    hasCloseBtn
                    className={props.classes.alert}
                />
                <Grid item xs={12} className={props.classes.controls}>
                    <NySubmitBtn className='space-right-1'/>
                    <NyResetBtn/>
                </Grid>
            </Grid>
        </NyForm>
    );
});
